import {
  canViewBilling,
  canConfirmOtherSale,
  canCountAssigned,
  canCreateOtherSale,
  canCreateShiftClose,
  canManageOtherSaleProducts,
  canManagePurchases,
  canManageUsers,
  canRecordWaste,
  canSubmitForms,
  canViewExpenses,
  canViewForms,
  canViewOtherSales,
  canViewOwnerDashboard,
  canViewVoidRequests,
} from '../auth/permissions';
import type { IconName } from '../components/ui/Icon';
import type { UserRole } from '../types/auth';
import { otherSalesHashTarget, POS_HASH } from '../utils/newProductRoute';

// Fase 24B: configuracion de navegacion (datos puros, sin JSX) — permite testear el agrupado + filtrado
// por rol sin renderizar. El filtrado (isVisible) es IDENTICO al de fases previas: solo experiencia; la
// seguridad real vive en el backend.
export type AppSection = 'dashboard' | 'owner-dashboard' | 'vender' | 'sales' | 'inventory' | 'waste' | 'counts' | 'purchases' | 'recipes' | 'shifts' | 'expenses' | 'other-sales' | 'operational-forms' | 'void-requests' | 'catalog-approvals' | 'forms' | 'integrations' | 'users' | 'stores' | 'billing' | 'nuevo-producto';

// `emphasis` marca el destino que el menú destaca (hoy solo «Vender»). Es presentación, no permiso.
export interface NavItem { id: AppSection; label: string; icon: IconName; isVisible: (role: UserRole | undefined) => boolean; emphasis?: boolean }
// Ola 4 (descubrimiento progresivo): un grupo `collapsible` arranca plegado — la configuración es
// administración, no operación diaria, y no debe competir visualmente con ella. La capa avanzada
// sigue a un toque; si la ruta activa vive dentro, el grupo se abre solo (deep links y refresh
// jamás esconden dónde estás). El estado abierto/cerrado es solo UX (localStorage); los permisos
// reales siguen viviendo en el backend.
export interface NavGroup { label: string; items: NavItem[]; collapsible?: boolean }

const isManagerOrOwner = (role: UserRole | undefined) => role === 'OWNER' || role === 'MANAGER';

/**
 * Rediseño · Bloque B: quién ve el destino «Vender». No es un permiso nuevo: es la conjunción de los
 * que YA hacen falta para vender hoy — entrar a Ventas Otros (`otherSales.view`), tener ahí la vista
 * «Venta rápida» (`otherSales.create`) y poder cobrar (`otherSales.confirm`, que es lo que exige el
 * servidor al abrir y cobrar una venta). Nunca es más amplio que lo que la pantalla ya ofrecía.
 */
export const canOpenPos = (role: UserRole | undefined) => canViewOtherSales(role) && canCreateOtherSale(role) && canConfirmOtherSale(role);

/**
 * Rediseño · Bloque E: quién abre «Nuevo producto». Tampoco es un permiso nuevo: es la conjunción de
 * los que YA hacen falta para dar de alta un producto hoy — entrar a Ventas Otros (`otherSales.view`),
 * donde vive el catálogo, y administrarlo (`otherSales.update`, lo que exige el servidor al crear).
 * Hoy, OWNER y MANAGER; el EMPLOYEE nunca. La tienda la sigue decidiendo el servidor.
 */
export const canCreateProduct = (role: UserRole | undefined) => canViewOtherSales(role) && canManageOtherSaleProducts(role);

export const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Principal',
    items: [
      { id: 'dashboard', label: 'Inicio', icon: 'home', isVisible: () => true },
      // Ola 2: el dashboard analítico se muda a "Reportes" (Inicio = estado y acción; Reportes =
      // análisis con calma). El id se conserva para no romper deep-links existentes.
      { id: 'owner-dashboard', label: 'Reportes', icon: 'dashboard', isVisible: canViewOwnerDashboard },
    ],
  },
  {
    label: 'Operación',
    items: [
      // Rediseño · Bloque B: «Vender» es un destino propio y el primero de Operación (P-030, P-031 §7).
      // P-075 (D-1, decisión del dueño): abre el punto de venta directo (`POS_HASH`), sin la página
      // intermedia, y queda resaltado mientras se vende. El punto de venta sigue siendo la vista de venta
      // de Ventas e ingresos: no se movió de ruta.
      { id: 'vender', label: 'Vender', icon: 'sell', isVisible: canOpenPos, emphasis: true },
      // «Ventas» única (Loyverse + POS en una lista) es una fase funcional posterior (B0-02): hasta
      // entonces siguen los dos destinos, con sus permisos de siempre.
      { id: 'sales', label: 'Ventas Loyverse', icon: 'reports', isVisible: isManagerOrOwner },
      // P-075 (D-2, decisión del dueño): «Ventas Otros» se llama «Ventas e ingresos» —lo cobrado con el punto
      // de venta y lo capturado a mano— y abre su Historial (D-1). La ruta, los datos y los permisos no cambian.
      { id: 'other-sales', label: 'Ventas e ingresos', icon: 'sales', isVisible: canViewOtherSales },
      { id: 'purchases', label: 'Compras de insumos', icon: 'purchases', isVisible: canManagePurchases },
      { id: 'expenses', label: 'Gastos', icon: 'expenses', isVisible: canViewExpenses },
      { id: 'inventory', label: 'Inventario', icon: 'inventory', isVisible: isManagerOrOwner },
      // F1: «Mermas» vive FUERA de Inventario: el EMPLOYEE registra lo que se perdió sin ver existencias.
      // B0-10: Mermas y Conteos no desaparecen del menú aunque el diseño aprobado no los dibuje.
      { id: 'waste', label: 'Mermas', icon: 'trash', isVisible: canRecordWaste },
      // F4: «Conteos» también vive FUERA de Inventario: el EMPLOYEE cuenta su lista a ciegas.
      { id: 'counts', label: 'Conteos', icon: 'count', isVisible: canCountAssigned },
      { id: 'shifts', label: 'Cortes de turno', icon: 'shift', isVisible: canCreateShiftClose },
      { id: 'operational-forms', label: 'Formularios', icon: 'forms', isVisible: canSubmitForms },
      { id: 'void-requests', label: 'Solicitudes', icon: 'void', isVisible: canViewVoidRequests },
      // Fase 26A: bandeja de aprobación de catálogo (proveedores/insumos propuestos) — OWNER/MANAGER.
      { id: 'catalog-approvals', label: 'Aprobaciones de catálogo', icon: 'check', isVisible: isManagerOrOwner },
    ],
  },
  {
    label: 'Configuración',
    collapsible: true,
    items: [
      { id: 'recipes', label: 'Recetas y costos', icon: 'recipes', isVisible: isManagerOrOwner },
      // "Diseño de formularios" (config) vs "Formularios" (responder, en Operación): nombres distintos
      // a propósito — antes ambos se llamaban casi igual y confundían.
      { id: 'forms', label: 'Diseño de formularios', icon: 'edit', isVisible: canViewForms },
      // P-075: «Integraciones» (como la llama UX_GUIDE): aquí vive también el lector de pagos (Zettle), que no
      // depende de Loyverse; con «Conexión Loyverse» un negocio sin Loyverse no lo encontraba.
      { id: 'integrations', label: 'Integraciones', icon: 'integrations', isVisible: isManagerOrOwner },
      { id: 'users', label: 'Usuarios', icon: 'users', isVisible: canManageUsers },
      { id: 'stores', label: 'Tiendas', icon: 'stores', isVisible: () => true },
      // ONB-007 (Macrofase B): el plan y la forma de pago del negocio. Es configuración (no operación
      // diaria) y solo aparece cuando el servidor dice que la facturación está encendida (ver
      // `visibleNavGroups`); con ella apagada, el menú queda exactamente como antes.
      { id: 'billing', label: 'Plan y facturación', icon: 'card', isVisible: canViewBilling },
    ],
  },
];

// Grupos con items visibles para un rol (grupos vacios se omiten).
// ZTV-04 (pre-client activation hardening): `loyverseConnected` esconde "Ventas Loyverse" SOLO
// cuando se sabe con certeza que el negocio no tiene la integración conectada (false). Con true o
// null (desconocido/cargando) el comportamiento es EXACTAMENTE el previo — un negocio con Loyverse
// nunca pierde su navegación por un parpadeo. "Conexión Loyverse" (Configuración) queda siempre
// visible: es el punto de descubrimiento de la integración opcional.
// ONB-007: `billingAvailable` muestra «Plan y facturación» SOLO cuando el servidor confirma que la
// facturación está encendida (`/api/billing/status`). Por default (apagada o aún no se sabe) no aparece:
// a diferencia de Loyverse, aquí lo seguro es no ofrecer un destino que todavía no existe.
export function visibleNavGroups(role: UserRole | undefined, loyverseConnected: boolean | null = null, billingAvailable = false): NavGroup[] {
  return NAV_GROUPS
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => item.isVisible(role)
        && !(item.id === 'sales' && loyverseConnected === false)
        && !(item.id === 'billing' && !billingAvailable)),
    }))
    .filter((group) => group.items.length > 0);
}

// Lista plana de secciones visibles para un rol (para tests y para resolver el titulo activo).
export function visibleSectionsForRole(role: UserRole | undefined, loyverseConnected: boolean | null = null, billingAvailable = false): AppSection[] {
  return visibleNavGroups(role, loyverseConnected, billingAvailable).flatMap((g) => g.items.map((i) => i.id));
}

const ALL_ITEMS: NavItem[] = NAV_GROUPS.flatMap((group) => group.items);

/**
 * Rediseño · Bloque E: pantallas con ruta propia que NO son destinos del menú. P-031 (§7) no crea
 * secciones nuevas en el menú, y «Nuevo producto» no lo es: es un paso del catálogo, al que se llega
 * desde el Inicio o desde el propio catálogo. Tiene ruta para que un enlace, una recarga o el Atrás la
 * reconstruyan.
 *
 * `shell` es el destino del menú que se resalta mientras se está ahí: el del catálogo, que vive en
 * Ventas Otros. `isVisible` es su guard, el mismo que decide si un acceso la ofrece.
 */
export interface RouteOutsideMenu { id: AppSection; shell: AppSection; isVisible: (role: UserRole | undefined) => boolean }
export const ROUTES_OUTSIDE_MENU: RouteOutsideMenu[] = [
  { id: 'nuevo-producto', shell: 'other-sales', isVisible: canCreateProduct },
];

/**
 * La sección que pide un hash (`#inventory/movements` → `inventory`). Lo que no se reconoce cae en
 * Inicio. Solo mira el primer segmento: el segundo es asunto de cada pantalla.
 */
export function sectionFromHash(hash: string): AppSection {
  const first = hash.replace('#', '').split('/')[0];
  return ALL_ITEMS.find((item) => item.id === first)?.id
    ?? ROUTES_OUTSIDE_MENU.find((route) => route.id === first)?.id
    ?? 'dashboard';
}

/** El destino del menú que se resalta en una sección: el suyo, o el de la pantalla de la que es parte. */
export function shellSectionOf(section: AppSection): AppSection {
  return ROUTES_OUTSIDE_MENU.find((route) => route.id === section)?.shell ?? section;
}

/**
 * P-075 (D-1). Lo que se resalta en el menú según la URL completa: mientras se está en el punto de venta
 * (`#other-sales/vender`), «Vender»; en el resto de Ventas e ingresos, «Ventas e ingresos».
 */
export function shellSectionForHash(section: AppSection, hash: string): AppSection {
  if (section === 'other-sales' && otherSalesHashTarget(hash).sell) return 'vender';
  return shellSectionOf(section);
}

/** P-075 (D-1). La dirección a la que lleva un destino del menú: «Vender» lleva al punto de venta. */
export function destinationOf(section: AppSection): string {
  return section === 'vender' ? POS_HASH : section;
}

/**
 * P-075 (D-1). `#vender` era la página intermedia «Vender abre el POS». Los enlaces guardados siguen
 * funcionando: llevan al punto de venta. Devuelve la dirección nueva, o `null` si no hay que cambiar nada.
 */
export function legacyHashRedirect(hash: string): string | null {
  return hash.replace('#', '').split('/')[0] === 'vender' ? POS_HASH : null;
}

/**
 * ¿Puede este rol abrir esta sección? La del menú, si la ve en el menú; una ruta fuera del menú, si
 * pasa su guard. Es lo que usan los accesos rápidos para no ofrecer nada que el rol no pueda abrir.
 */
export function canOpenSection(role: UserRole | undefined, section: AppSection): boolean {
  const route = ROUTES_OUTSIDE_MENU.find((candidate) => candidate.id === section);
  return route ? route.isVisible(role) : visibleSectionsForRole(role).includes(section);
}

/**
 * Rediseño · Bloque B: las pestañas de la barra inferior del teléfono.
 *
 * Son las del diseño aprobado (P-021, P-022; B0-09) — Inicio · Ventas · Inventario · Reportes — más
 * «Más», que no es un destino: abre el menú completo y lo dibuja el shell. Aquí NO se decide quién
 * ve qué: cada pestaña apunta a secciones que ya existen y solo aparece si el rol ve alguna.
 *
 * «Ventas» lleva dos candidatas porque la lista única de ventas todavía no existe (fase funcional
 * posterior, B0-02): abre la primera que el rol ve, en el orden del menú — Ventas Loyverse si está
 * a la vista y, si no, Ventas Otros. La otra sigue en el menú.
 *
 * El icono es el de la pestaña en el diseño aprobado (P-021), no el del destino al que lleva: así
 * «Ventas» se ve igual abra una lista o la otra, y no cambia cuando se sabe si hay Loyverse.
 */
export interface BottomNavTab { id: string; label: string; icon: IconName; sections: AppSection[] }
export const BOTTOM_NAV_TABS: BottomNavTab[] = [
  { id: 'home', label: 'Inicio', icon: 'home', sections: ['dashboard'] },
  { id: 'sales', label: 'Ventas', icon: 'bars', sections: ['sales', 'other-sales'] },
  { id: 'inventory', label: 'Inventario', icon: 'inventory', sections: ['inventory'] },
  { id: 'reports', label: 'Reportes', icon: 'document', sections: ['owner-dashboard'] },
];

export interface VisibleBottomNavTab { id: string; label: string; icon: IconName; section: AppSection }

// Pestañas que le tocan a un rol, cada una ya resuelta a la sección que abre. Una pestaña sin
// ninguna sección visible no se dibuja (un EMPLOYEE no tiene Inventario ni Reportes): sus destinos,
// y todos los demás, están en «Más».
export function visibleBottomNavTabs(role: UserRole | undefined, loyverseConnected: boolean | null = null): VisibleBottomNavTab[] {
  const visible = visibleSectionsForRole(role, loyverseConnected);
  return BOTTOM_NAV_TABS.flatMap((tab) => {
    const section = tab.sections.find((candidate) => visible.includes(candidate));
    return section ? [{ id: tab.id, label: tab.label, icon: tab.icon, section }] : [];
  });
}
